import React, {useState, useEffect, useRef} from 'react';
import { Link, Stack, Alert, Box,CircularProgress, OutlinedInput, InputAdornment,  Divider, Grid2 as Grid, Typography, FormControl, InputLabel, Select, MenuItem} from '@mui/material';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import {Search} from '@mui/icons-material';
import ReportListExportSession from './ReportListExportSection';
import { DataGrid } from '@mui/x-data-grid';
import { useErrorBoundary } from 'react-error-boundary';
import IePacReport from '../../../types/reports/iePacReport';
import { debounce, filter} from 'lodash';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';
import IndependentSpendingReportsTable from './IndependentSpendingReportsTable';

const IndependentExpenditurePacReports = (): JSX.Element => {
    
    const dropDownYears = Array.from(new Array(11), (val, index) => new Date().getFullYear() - index);
    const [reports, setReports] = useState<IePacReport[]>();
    const [tableRows, setTableRows] = useState<IePacReport[]>();
    const [reportYear, setReportYear] = useState(new Date().getMonth()===0?new Date().getFullYear()-1: new Date().getFullYear());
    const[filersDropdown, setFilersDropdown] = useState([]);
    const[candidatesDropdown, setCandidatesDropdown] = useState([]);
    const[summary, setSummary] = useState('No reports found.');
    const[currentFiler, setCurrentFiler] = useState('All');
    const[currentCandidate, setCurrentCandidate] = useState('All');
    const [startDate, setStartDate] = useState(''); 
    const [endDate, setEndDate] = useState('');
    const startInputRef = useRef<HTMLInputElement>(null);
    const endInputRef = useRef<HTMLInputElement>(null);
    const [isLoading, setIsLoading] = useState(true);

    const [excelExportUrl, setExcelExportUrl] = useState('');
    const [txtExportUrl, setTxtExportUrl] = useState('');

    const { showBoundary } = useErrorBoundary(); 

      const updateReportYear = (event: any) => {
        setReportYear(event.target.value);
        setCurrentFiler('All');
        setCurrentCandidate('All');
        if(startInputRef.current)startInputRef.current.value = '';
        if(endInputRef.current)endInputRef.current.value = '';

      };

      const updateFiler = (event: any) => { 
        setCurrentFiler(event.target.value);
        
      };

      const updateCandidate = (event: any) => {
        setCurrentCandidate(event.target.value);
      }

      const filterDate = debounce((event: any) => {
        const id = event.target.id;  // Capture values immediately
        const value = event.target.value;
        
        id === 'startDate' ? setStartDate(value) : setEndDate(value);
      }, 800);

      const handleFilterChange = () => {
        const filtered = reports?.filter((row) => {

          const matchesFiler = currentFiler === 'All' || row.committeeName.includes(currentFiler);
          const matchesCandidate = currentCandidate === 'All' || row.candidateListing.includes(currentCandidate) ;
          

          let matchesStartDate = true;
          let matchesEndDate = true;

          if(startDate.length > 1 && (new Date(row.startDateDisplay) < new Date(startDate) )){
            matchesStartDate = false;
          }

          if(endDate.length > 1 && (new Date(row.endDateDisplay) > new Date(endDate) )){
            matchesEndDate = false;
          }  

          return matchesFiler && matchesCandidate && matchesStartDate && matchesEndDate;
        });
      
        setTableRows(filtered);
        if(filtered){
          filtered.length > 0 ? setSummary(`Viewing 1 through ${filtered.length} of ${reports?.length} reports`) : setSummary("No reports found.");
        }else{
          setSummary("No reports found.");
        }
        
      };
      
      useEffect(() => {
        getIndependentExpenditurePacReports();
        getFilersDropdown();
        getCandidatesDropdown();
      }, [reportYear]);

      useEffect(() => {
        handleFilterChange();
      }, [currentFiler, currentCandidate, startDate, endDate]);
    

      const getFilersDropdown = async () => {
        try{
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/iepacs/committeeNames/${reportYear}`)
          .then(result => {
            if (!result.ok) {
              showBoundary(new Error('Failed to get the Independent Expenditure PAC filers.'));
            }
            return result.json();
          })
          .then(result => {
            setFilersDropdown(result);
          });
        }catch(error){
            console.error('Uncaught Error:', error);
        }
      }

      const getCandidatesDropdown = async () => {
        try{
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/iepacs/candidates/${reportYear}`)
          .then(result => {
            if (!result.ok) {
              showBoundary(new Error('Failed to get the Independent Expenditure PAC candidates.'));
            }
            return result.json();
          })
          .then(result => {
            setCandidatesDropdown(result);
          });
        }catch(error){
            console.error('Uncaught Error:', error);
        }
      }
      
      const getSummary = async () => {
        try{
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/iepacs/reports/summary/${reportYear}`)
          .then(result => {
            if (!result.ok) {
              showBoundary(new Error('Failed to get the Independent Expenditure PAC reports summary.'));
            }
            return result.json();
          })
          .then(result => {
            let count = result.count;
            if (count === 0){
              setSummary("No reports found.");
            }
            else{
              setSummary(`Viewing 1 through ${count} of ${count} reports, ${result.totalDisplay}`);
            }      
          });
        }catch(error){
            console.error('Uncaught Error:', error);
        }
      }


      const getIndependentExpenditurePacReports = async () => {

        setExcelExportUrl(`${process.env.REACT_APP_API_BASE_URL}/miscreports/iepacs/excelOutput/${reportYear}`);
        setTxtExportUrl(`${process.env.REACT_APP_API_BASE_URL}/miscreports/iepacs/textOutput/${reportYear}`);
        
        try{
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/iepacs/reports/${reportYear}`)
          .then(result => {
            if (!result.ok) {
              showBoundary(new Error('Failed to get the Independent Expenditure PAC reports.'));
            }
            return result.json();
          })
          .then(result => {
            setTableRows(result);
            setReports(result);
            getSummary();
            setIsLoading(false);
          });
        }catch(error){
            console.error('Uncaught Error:', error);
        }
      }

    return (
      <Main>
        <Container>
        <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
            variant="h5"
            align={'left'}
            data-aos={'fade-up'}
            gutterBottom
            sx={{
                fontWeight: 700,
            }}
            paddingTop={3}
            marginBottom={2}
            >
            Independent Expenditure PAC Reports
        </Typography>
        <Divider/>
        <Grid container marginBottom={2} marginTop={3}>
          <Grid size={12} marginBottom={4}>
              <Typography marginBottom={1} sx={{textIndent: 10}} component={'p'} variant='body1'>A group organized to raise money for the purpose of making independent expenditures is required to organize as an Independent Expenditure Political Action Committee (IEPAC). These committees are sometimes called "superpacs." OCPF has issued regulations (<Link href='/Legal/Regulations' target='_blank'>970 CMR 2.17</Link>) and an interpretive bulletin (<Link href='https://ocpf2.blob.core.windows.net/pdf/legaldocs/IB-10-03.pdf' target='_blank'>IB-10-03</Link>) to provide guidance to these groups.</Typography>
              <Typography marginBottom={1} sx={{textIndent: 10}} component={'p'} variant='body1'>In addition to filing periodic campaign finance reports, if IEPACs make independent expenditures of more than $250 to support or oppose Massachusetts candidates during the calendar year, the campaign finance law requires them to file reports disclosing the independent expenditure activity in accordance with M.G.L. Chapter 55, <Link href='/Legal/CampaignFinanceLaw' target='_blank'>Section 18A</Link>. In addition to the expenditure information, IEPACs are also required to disclose their contribution information as well as the other campaign finance information required to be disclosed by <Link href='/Legal/CampaignFinanceLaw' target='_blank'>section 18</Link>.</Typography>
              <Typography sx={{textIndent: 10}}  component={'p'} variant='body1'>IEPAC reports are displayed on this page in chronological order, newest to oldest. The reports can be filtered by year, by making a selection from the dropdown list provided. Within each year, the reports can be filtered by a date range, the IEPAC making the expenditure, the subject (candidate) of the expenditure, or the position (support or oppose) taken by the filer.</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Stack direction={"row"} spacing={1} 
                sx={{ mb: 1}}
                >
                   <FormControl sx={{ m: 1,minWidth: 120  }} size="small">
                    <InputLabel id="year-id">Year</InputLabel>
                    <Select 
                        labelId="year-id"
                        id="year-select"
                        aria-labelledby='year-id'
                        sx={{fontSize: 'smaller'}} 
                        label="Report Year"
                        value={reportYear}
                        onChange={updateReportYear}
                    >
                    {
                        dropDownYears.map((year) =>
                            <MenuItem value={year} key={year} >{year}</MenuItem>
                        )
                    }
                    </Select>
                </FormControl>
                  <FormControl size="small" sx={{ m: 1, p:0, maxWidth: 100 }}>
                        <OutlinedInput
                            aria-label='start date'
                            placeholder={"Start Date"}
                            inputRef={startInputRef}
                            id="startDate"
                            type='text'
                            onChange={filterDate}
                            sx={{ fontSize: 'smaller' }}
                        />

                </FormControl>
                <FormControl size="small" sx={{ m: 1, p:0, maxWidth: 100 }}>
                        <OutlinedInput
                            aria-label='end date'
                            placeholder={"End Date"}
                            inputRef={endInputRef}
                            type='text'
                            id="endDate"
                            onChange={filterDate}
                            sx={{ fontSize: 'smaller' }}
                        />

                </FormControl>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <InputLabel id='filer-id'>Filer</InputLabel>
                    <Select 
                        labelId="filer-id"
                        id="filer-select"
                        aria-labelledby='filer-id'
                        sx={{fontSize: 'smaller'}} 
                        label="Type"
                        value={currentFiler}
                        onChange={updateFiler}
                    >
                      <MenuItem value='All' key='All' >All</MenuItem>
                        {
                            filersDropdown.map((filer) =>
                                <MenuItem value={filer} key={filer} >{filer}</MenuItem>
                            )
                        }
                        
                    </Select>
                </FormControl>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <InputLabel id="candidate-id">Candidate</InputLabel>
                    <Select 
                        labelId="candidate-id"
                        id="candidate-select"
                        aria-labelledby='candidate-id'
                        sx={{fontSize: 'smaller'}} 
                        label="Type"
                        value={currentCandidate}
                        onChange={updateCandidate}
                    >
                       <MenuItem value='All' key='All' >All</MenuItem>
                        {
                            candidatesDropdown.map((candidate) =>
                                <MenuItem value={candidate} key={candidate} >{candidate}</MenuItem>
                            )
                        }
                    </Select>
                </FormControl>
            </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <ReportListExportSession excelExportUrl={excelExportUrl} txtExportUrl={txtExportUrl}></ReportListExportSession>
        </Grid>
        <Grid size={12}>
            <Alert icon={false} severity="info"
            sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                '.MuiAlert-message': { 
                padding: '4px',
                } 
            }}
        >
            {summary}
            </Alert>
        </Grid>
      </Grid>
       {/*report table*/}
       <IndependentSpendingReportsTable tableRows={tableRows} type='iePac'/>
       {/*loading indicator*/}
              <CircularProgress
                  aria-label='loading-indicator'
                   size={50}
                   className={isLoading? "": "hidden"}
                   sx={{
                     position: "fixed",
                     left: "50%",
                     top: "20%",
                     transform: "translate(-50%, -50%)",
                     zIndex: 2
                    }}
               />
    </Container>
    </Main>
  );
};

export default IndependentExpenditurePacReports;
